/* Presentation helpers. British English.

   The backend hands us ISO 8601 with an offset and raw seconds; deciding what
   a human should read is the UI's job. Times render in the tenant's timezone
   (see lib/timezone.js), so a customer sees their own local time. */

import { formatter, ymd } from './timezone.js';

/** "Today 14:22", "Yesterday 17:20", "13 Jul 09:14" — in the display timezone. */
export function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';

  const time = formatter('time', { hour: '2-digit', minute: '2-digit' }).format(d);

  const day = ymd(d);
  const today = ymd(new Date());
  if (day === today) return `Today ${time}`;
  if (day === prevDay(today)) return `Yesterday ${time}`;

  return `${formatter('daymon', { day: 'numeric', month: 'short' }).format(d)} ${time}`;
}

/** The calendar date before `ymdStr` (YYYY-MM-DD), timezone-agnostic. */
function prevDay(ymdStr) {
  const [y, m, d] = ymdStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
}

/** 157 → "2:37". Talk time, so 0 means it was never answered. */
export function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * UK numbers, grouped the way people read them aloud.
 *
 * The PBX is inconsistent about form: a DID arrives as 441933833413, a caller
 * as 07827729043, an international caller as 00351960061312. Normalise to
 * national form first, then group.
 */
export function formatNumber(value) {
  if (!value) return 'Withheld';

  let n = String(value).trim();
  if (n === '' || /^(anonymous|unknown|withheld)$/i.test(n)) return 'Withheld';
  if (n.length <= 5 || !/^[+0-9]+$/.test(n)) return n;   // an extension, or VMU-9091

  // 00351… → +351…  (dialled international prefix)
  if (n.startsWith('00')) n = `+${n.slice(2)}`;

  // 441933833413 / +441933833413 → 01933833413. Our own numbers arrive in
  // E.164 without the +, which would otherwise read as gibberish.
  const e164 = n.replace(/^\+/, '');
  if (/^44\d{10}$/.test(e164)) n = `0${e164.slice(2)}`;

  if (/^07\d{9}$/.test(n)) return `${n.slice(0, 5)} ${n.slice(5)}`;          // mobile
  if (/^02\d{9}$/.test(n)) return `${n.slice(0, 3)} ${n.slice(3, 7)} ${n.slice(7)}`; // London &c.
  if (/^01(1\d|\d1)\d{7}$/.test(n)) return `${n.slice(0, 4)} ${n.slice(4, 7)} ${n.slice(7)}`; // 0113, 0121
  if (/^01\d{9}$/.test(n)) return `${n.slice(0, 5)} ${n.slice(5)}`;          // 01933 833413
  if (/^0(3|8)\d{9}$/.test(n)) return `${n.slice(0, 4)} ${n.slice(4, 7)} ${n.slice(7)}`;

  return n;
}

/** 3955 → "1h 6m", 95 → "2m", 0 → "0m". For totals, not call durations. */
export function formatMinutes(seconds) {
  const total = Math.round((seconds || 0) / 60);
  if (total < 60) return `${total}m`;
  return `${Math.floor(total / 60)}h ${total % 60}m`;
}

export const STATUS_LABELS = {
  answered: 'Answered',
  // Only an extension user sees this: the call rang them, a colleague took
  // it. It is emphatically not their miss.
  answered_elsewhere: 'Answered by a colleague',
  // Nobody picked up, but the caller left a message — a miss with a callback
  // attached, which is worth distinguishing from a miss that left nothing.
  voicemail: 'Voicemail',
  missed: 'Missed',
  unknown: 'Unknown',
};

/* Pill tones are functional — mint connected, amber needs attention, red
   missed. A colleague answering is a success, so it reads mint, not red. */
export const STATUS_TONES = {
  answered: 'answered',
  answered_elsewhere: 'answered',
  voicemail: 'voicemail',
  missed: 'missed',
  unknown: 'offline',
};
